/*
 * Size chart popup controller + Teeinblue trigger injection.
 *
 * The popup itself (#mc-size-chart-popup) is rendered server-side by
 * snippets/mc-size-chart.liquid ONLY when the product is apparel and has a
 * matching `size_chart` metaobject. So:
 *   - if the popup is absent, this product has no size chart → we inject no
 *     Teeinblue trigger and there is nothing to open;
 *   - the native variant-picker trigger and the Teeinblue-injected trigger both
 *     carry class `mc-size-chart-trigger` and open the same popup.
 *
 * Opens on click/tap (and keyboard Enter/Space, since the triggers are buttons).
 * Closes on the close button, backdrop click, or Escape.
 */
(() => {
  'use strict';

  const POPUP_ID = 'mc-size-chart-popup';
  const TRIGGER_SELECTOR = '.mc-size-chart-trigger';

  const UNIT_KEY = 'mc-size-chart-unit';

  const getPopup = () => document.getElementById(POPUP_ID);

  let lastFocused = null;
  let hideTimer = null;

  // Switch the size chart between metric (cm) and imperial (in). No-op if the
  // requested unit has no toggle button (i.e. that image wasn't provided).
  const setUnit = (unit) => {
    const popup = getPopup();
    if (!popup || (unit !== 'metric' && unit !== 'imperial')) return;
    if (!popup.querySelector(`[data-mc-unit="${unit}"]`)) return;
    popup.setAttribute('data-unit', unit);
    popup.querySelectorAll('[data-mc-unit]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-mc-unit') === unit ? 'true' : 'false');
    });
    try { localStorage.setItem(UNIT_KEY, unit); } catch (e) { /* ignore */ }
  };

  const applySavedUnit = () => {
    try {
      const saved = localStorage.getItem(UNIT_KEY);
      if (saved) setUnit(saved);
    } catch (e) { /* ignore */ }
  };

  const openPopup = () => {
    const popup = getPopup();
    if (!popup || popup.classList.contains('is-open')) return;
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    lastFocused = document.activeElement;
    applySavedUnit();
    popup.hidden = false;
    // next frame so the open transition runs
    requestAnimationFrame(() => popup.classList.add('is-open'));
    document.documentElement.classList.add('mc-size-chart-lock');
    const closeBtn = popup.querySelector('[data-mc-size-chart-close]');
    if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
  };

  const closePopup = () => {
    const popup = getPopup();
    if (!popup || popup.hidden) return;
    popup.classList.remove('is-open');
    document.documentElement.classList.remove('mc-size-chart-lock');
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      if (!popup.classList.contains('is-open')) popup.hidden = true;
    }, 260);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest) return;
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      event.preventDefault();
      openPopup();
      return;
    }
    const unitBtn = event.target.closest('[data-mc-unit]');
    if (unitBtn) {
      event.preventDefault();
      setUnit(unitBtn.getAttribute('data-mc-unit'));
      return;
    }
    if (event.target.closest('[data-mc-size-chart-close]')) {
      event.preventDefault();
      closePopup();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePopup();
  });

  // ---- Teeinblue trigger injection ----
  const OPTION_SELECTOR = '.tee-option--size, .tee-option';
  const TITLE_SELECTOR = '.tee-option__title';
  const INITIALIZED_ATTRIBUTE = 'data-mc-size-chart-ready';

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const initializeOption = (option) => {
    if (!(option instanceof HTMLElement) || option.hasAttribute(INITIALIZED_ATTRIBUTE)) return;
    // No popup for this product → no size chart → don't inject a trigger.
    if (!getPopup()) return;

    const title = option.querySelector(`:scope > ${TITLE_SELECTOR}`) || option.querySelector(TITLE_SELECTOR);
    if (!(title instanceof HTMLElement)) return;

    const isSizeOption = option.classList.contains('tee-option--size') || normalize(title.textContent) === 'size';
    if (!isSizeOption) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'mc-tee-size-chart-trigger mc-size-chart-trigger';
    button.setAttribute('aria-controls', POPUP_ID);
    button.setAttribute('aria-haspopup', 'dialog');
    button.textContent = 'Size chart';

    option.classList.add('mc-tee-size-chart-option');
    option.append(button);
    option.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
  };

  const initializeAll = (root = document) => {
    if (!getPopup()) return;
    if (root instanceof Element && root.matches(OPTION_SELECTOR)) initializeOption(root);
    root.querySelectorAll?.(OPTION_SELECTOR).forEach(initializeOption);
  };

  const scheduleInitialize = () => window.requestAnimationFrame(() => initializeAll());

  initializeAll();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitialize, { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initializeAll(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.setTimeout(scheduleInitialize, 250);
  window.setTimeout(scheduleInitialize, 1000);
  window.setTimeout(scheduleInitialize, 2500);
})();
